/**
 * Integrations (`/integrations`): how other systems reach Wynxx Discover as the signed-in person. API reference, API
 * guide and API keys need `api.key.manage.own`; MCP needs `mcp.token.manage.own` while MCP is on. A tab shows only to
 * whoever may use it; the open tab is kept in the address (`?tab=`).
 */

import { BookOpenText, KeyRound, Plug, ScrollText, Waypoints } from 'lucide-react';

import { useSession } from '@/session/SessionContext';
import { PageHead } from '@/ui/PageHead';
import { StateCard } from '@/ui/StateCard';
import { type TabItem, Tabs } from '@/ui/Tabs';

import { ApiGuideTab } from './ApiGuideTab';
import { ApiKeysTab } from './ApiKeysTab';
import { ApiReferenceTab } from './ApiReferenceTab';
import { McpTab } from './McpTab';

function tabsFor(api: boolean, mcp: boolean): TabItem[] {
  return [
    ...(api
      ? [
          { key: 'reference', label: 'API reference', icon: <BookOpenText aria-hidden="true" /> },
          { key: 'guide', label: 'API guide', icon: <ScrollText aria-hidden="true" /> },
          { key: 'keys', label: 'API keys', icon: <KeyRound aria-hidden="true" /> },
        ]
      : []),
    ...(mcp ? [{ key: 'mcp', label: 'MCP', icon: <Plug aria-hidden="true" /> }] : []),
  ];
}

function Body({ tab }: { tab: string }) {
  if (tab === 'guide') return <ApiGuideTab />;
  if (tab === 'keys') return <ApiKeysTab />;
  if (tab === 'mcp') return <McpTab />;
  return <ApiReferenceTab />;
}

export function IntegrationsPage() {
  const { can, mcpEnabled } = useSession();
  const tabs = tabsFor(can('api.key.manage.own'), can('mcp.token.manage.own') && mcpEnabled);
  return (
    <>
      <PageHead title="Integrations" subtitle="Connect other systems and AI tools to Wynxx Discover, as you." />
      {tabs.length === 0 ? (
        <StateCard icon={<Waypoints aria-hidden="true" />} title="Nothing to connect here">
          <p>Your role gives no API keys or MCP access.</p>
        </StateCard>
      ) : (
        <div className="card list-card">
          <Tabs label="Integrations" card param="tab" tabs={tabs}>
            {(key) => <Body tab={key} />}
          </Tabs>
        </div>
      )}
    </>
  );
}
